import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Domain-aware root router.
 * 
 * Maps production hostnames to their portal entry points:
 *   hire.fork.sa  → /dashboard (ForkHire)
 *   seek.fork.sa  → /seek      (ForkSeek)
 *   admin.fork.sa → /super-admin (ForkAdmin)
 * 
 * For dev/preview/unknown hosts, falls through to the Landing page.
 */

const DOMAIN_MAP: Record<string, string> = {
  "hire.fork.sa": "/dashboard",
  "seek.fork.sa": "/seek",
  "admin.fork.sa": "/super-admin",
};

export function getDomainPortal(): string | null {
  const hostname = window.location.hostname.toLowerCase();
  const target = DOMAIN_MAP[hostname];
  // Unknown and development hosts show the landing page.
  if (!target || target === "/") return null;
  return target;
}

export function getPortalFromHostname(): "hire" | "seek" | "admin" | null {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname === "hire.fork.sa") return "hire";
  if (hostname === "seek.fork.sa") return "seek";
  if (hostname === "admin.fork.sa") return "admin";
  return null;
}

/**
 * Component used at the root "/" route.
 * If on a portal-specific domain, immediately redirects.
 * Otherwise renders children (Landing page).
 */
export function DomainRedirect({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const target = getDomainPortal();

  useEffect(() => {
    if (target) {
      navigate(target, { replace: true });
    }
  }, [target, navigate]);

  // If redirecting, show nothing briefly
  if (target) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
}
